import axios from 'axios'
import { coerceNfhsIsInteractive, nfhsDigitalReaderUrl, nfhsPublicationPdfUrl } from '@/utils/nfhsDigital.js'

/**
 * Wrapper around our learn-server `/nfhs-digital` routes.
 * The browser never talks to api.nfhsdigital.org and never holds the NFHS key —
 * the RefReps session cookie authenticates every call.
 */

const BASE_PATH = '/nfhs-digital'

export const NFHS_PUBLICATION_PLACEHOLDER =
  'https://host.refreps.com/images/placeholder-image.png'

export const NFHS_LOGIN_URL = 'https://rules.nfhsdigital.org/'
export const NFHS_REGISTER_URL = 'https://rules.nfhsdigital.org/register'
export const NFHS_READER_BASE_URL = 'https://rules.nfhsdigital.org'

function baseUrl() {
  return `${import.meta.env.VITE_BASE_LINK}${BASE_PATH}`
}

/** Quiet note when someone opens a book from RefReps. Failures stay invisible. */
export function recordNfhsBookOpen({ bookId, bookTitle, mode } = {}) {
  const id = String(bookId || '').trim()
  if (!id) return
  axios.post(`${baseUrl()}/book-opens`, {
    bookId: id,
    bookTitle: String(bookTitle || '').trim().slice(0, 300),
    mode: mode === 'preview' ? 'preview' : 'read',
  }, requestConfig()).catch(() => {})
}

function requestConfig(params) {
  return {
    withCredentials: true,
    headers: { 'Content-Type': 'application/json' },
    ...(params ? { params } : {}),
  }
}

/**
 * 401 = not logged in, 503 = NFHS Digital not configured on the server.
 * @param {unknown} err axios error
 */
export function parseNfhsDigitalError(err) {
  const status = err?.response?.status ?? null
  const data = err?.response?.data
  let message = data?.message || data?.error || err?.message || 'Request failed'
  if (status === 401) message = 'Sign in to RefReps to view NFHS publications.'
  if (status === 503) message = 'NFHS Digital is not configured yet.'
  const e = new Error(String(message))
  e.status = status
  e.notLoggedIn = status === 401
  e.notConfigured = status === 503
  return e
}

/** Cover with placeholder fallback. @param {Record<string, unknown>} publication */
export function nfhsPublicationCoverSrc(publication) {
  const src = String(publication?.image || publication?.cover || '').trim()
  return src || NFHS_PUBLICATION_PLACEHOLDER
}

/**
 * Backend already normalizes cards; this only guards missing ids and
 * fills the reader URL / sport label so the grid can render blindly.
 * @param {Record<string, unknown> | null | undefined} raw
 */
export function normalizeNfhsPublication(raw) {
  if (!raw || typeof raw !== 'object') return null
  const id = raw.id != null ? String(raw.id) : ''
  if (!id) return null
  const title = String(raw.title ?? '').trim() || 'Untitled publication'
  const sport = String(raw.sport ?? '').trim()
  const rawPrice = raw.price ?? raw.unitPrice
  const priceNumber = Number(rawPrice)
  const isInteractive = coerceNfhsIsInteractive(raw.isInteractive)
  const pdfURL = nfhsPublicationPdfUrl(raw) || null
  const routed = {
    ...raw,
    id,
    isInteractive,
    pdfURL,
    readerPath: raw.readerPath || null,
    readerUrl: raw.readerUrl,
  }
  return {
    ...raw,
    id,
    title,
    sport,
    sportLabel: String(raw.sportLabel ?? '').trim() || sport || 'General',
    image: String(raw.image ?? raw.cover ?? '').trim(),
    cover: String(raw.cover ?? raw.image ?? '').trim(),
    publisher: String(raw.publisher ?? '').trim(),
    isInteractive,
    features: raw.features && typeof raw.features === 'object' ? raw.features : null,
    pdfURL,
    readerPath: raw.readerPath || null,
    readerUrl: nfhsDigitalReaderUrl(routed),
    status: raw.status ?? null,
    isReadable: raw.isReadable !== false && raw.status !== 'FUTURE_ASSIGNED',
    // Prefer NFHS Digital /books `price` (dollars). Null when missing.
    price: Number.isFinite(priceNumber) && priceNumber >= 0 ? priceNumber : null,
  }
}

/** @param {unknown} list */
export function normalizeNfhsPublications(list) {
  if (!Array.isArray(list)) return []
  const seen = new Set()
  const out = []
  for (const raw of list) {
    const publication = normalizeNfhsPublication(raw)
    if (!publication || seen.has(publication.id)) continue
    seen.add(publication.id)
    out.push(publication)
  }
  return out
}

/** Filter by title / sport / publisher. */
export function filterNfhsPublications(publications, query) {
  const q = String(query ?? '').trim().toLowerCase()
  if (!q) return Array.isArray(publications) ? publications : []
  return (Array.isArray(publications) ? publications : []).filter((p) =>
    [p.title, p.sportLabel, p.sport, p.publisher]
      .filter(Boolean)
      .some((field) => String(field).toLowerCase().includes(q))
  )
}

/**
 * The logged-in user's assigned publications, already hydrated by the backend.
 * Wrapper around NFHS `POST /users/lookup` + `GET /books` — not an official NFHS path.
 */
export async function fetchMyNfhsPublications() {
  try {
    const { data } = await axios.get(
      `${baseUrl()}/my-publications`,
      requestConfig()
    )
    return {
      success: data?.success !== false,
      hasAccount: Boolean(data?.hasAccount),
      user: data?.user ?? null,
      publications: normalizeNfhsPublications(data?.publications),
      total: Number(data?.total) || 0,
      loginUrl: data?.loginUrl || NFHS_LOGIN_URL,
      registerUrl: data?.registerUrl || NFHS_REGISTER_URL,
      readerBaseUrl: data?.readerBaseUrl || NFHS_READER_BASE_URL,
    }
  } catch (err) {
    throw parseNfhsDigitalError(err)
  }
}

/**
 * Assignable catalog.
 * @param {{ lifecycle?: 'current' | 'previous' | 'future', sport?: string }} [query]
 */
export async function fetchNfhsBooks(query = {}) {
  const params = {}
  if (query.lifecycle) params.lifecycle = query.lifecycle
  if (query.sport) params.sport = query.sport
  try {
    const { data } = await axios.get(
      `${baseUrl()}/books`,
      requestConfig(Object.keys(params).length > 0 ? params : undefined)
    )
    return {
      success: data?.success !== false,
      total: Number(data?.total) || 0,
      books: normalizeNfhsPublications(data?.books),
    }
  } catch (err) {
    throw parseNfhsDigitalError(err)
  }
}

/** Look up another person (instructor assigning to a learner). @param {string} email */
export async function lookupNfhsUser(email) {
  try {
    const { data } = await axios.post(
      `${baseUrl()}/users/lookup`,
      { email: String(email ?? '').trim() },
      requestConfig()
    )
    const user = data?.user ?? null
    return {
      success: data?.success !== false,
      user: user
        ? { ...user, publications: normalizeNfhsPublications(user.publications) }
        : null,
    }
  } catch (err) {
    throw parseNfhsDigitalError(err)
  }
}

/**
 * @param {{ email: string, bookId: string, companyID: string, organizationId?: string, sendEmail?: boolean }} payload
 */
export async function assignNfhsBook(payload) {
  const subId = String(payload?.companyID || payload?.subId || '').trim()
  try {
    const { data } = await axios.post(
      `${baseUrl()}/books/assign/${encodeURIComponent(subId)}`,
      payload,
      requestConfig()
    )
    return data
  } catch (err) {
    throw parseNfhsDigitalError(err)
  }
}

/**
 * @param {{ email: string, bookIds: string[], organizationId?: string, sendEmail?: boolean }} payload
 */
export async function assignNfhsBooksMultiple(payload) {
  try {
    const { data } = await axios.post(
      `${baseUrl()}/books/assign-multiple`,
      payload,
      requestConfig()
    )
    return data
  } catch (err) {
    throw parseNfhsDigitalError(err)
  }
}

/**
 * @param {{ users: string[], bookIds: string[], organizationId?: string, sendEmail?: boolean }} payload
 */
export async function assignNfhsBooksBulk(payload) {
  try {
    const { data } = await axios.post(
      `${baseUrl()}/books/assign-bulk`,
      payload,
      requestConfig()
    )
    return data
  } catch (err) {
    throw parseNfhsDigitalError(err)
  }
}

/**
 * @param {{ email: string, bookId: string, companyID: string, organizationId?: string, sendEmail?: boolean }} payload
 */
export async function unassignNfhsBook(payload) {
  const subId = String(payload?.companyID || payload?.subId || '').trim()
  try {
    const { data } = await axios.post(
      `${baseUrl()}/books/unassign/${encodeURIComponent(subId)}`,
      payload,
      requestConfig()
    )
    return data
  } catch (err) {
    throw parseNfhsDigitalError(err)
  }
}

/**
 * @param {{ page?: number, limit?: number, organizationId?: string }} [query]
 */
export async function fetchNfhsUsers(query = {}) {
  try {
    const { data } = await axios.get(`${baseUrl()}/users`, requestConfig(query))
    return data
  } catch (err) {
    throw parseNfhsDigitalError(err)
  }
}

/** @param {{ organizationId?: string }} [query] */
export async function fetchNfhsSeatLicenses(query = {}) {
  try {
    const { data } = await axios.get(
      `${baseUrl()}/seat-licenses`,
      requestConfig(query)
    )
    return data
  } catch (err) {
    throw parseNfhsDigitalError(err)
  }
}

/**
 * @param {{
 *   email: string, firstName: string, lastName: string,
 *   state?: string, phone?: string, organizationId?: string, sendEmail?: boolean,
 * }} payload
 */
export async function registerNfhsUser(payload) {
  try {
    const { data } = await axios.post(
      `${baseUrl()}/users/register`,
      payload,
      requestConfig()
    )
    return data
  } catch (err) {
    throw parseNfhsDigitalError(err)
  }
}

/** @param {string} companyID */
export async function fetchOrgNfhsAddon(companyID) {
  try {
    const { data } = await axios.get(
      `${baseUrl()}/org-addon`,
      requestConfig({ companyID })
    )
    return data
  } catch (err) {
    throw parseNfhsDigitalError(err)
  }
}

/**
 * @param {{ companyID: string, bookId?: string }} query
 */
export async function fetchOrgNfhsAssignments(query = {}) {
  const params = { companyID: query.companyID }
  if (query.bookId) params.bookId = query.bookId
  try {
    const { data } = await axios.get(
      `${baseUrl()}/org-assignments`,
      requestConfig(params)
    )
    return {
      success: data?.success !== false,
      assignments: Array.isArray(data?.assignments) ? data.assignments : [],
    }
  } catch (err) {
    throw parseNfhsDigitalError(err)
  }
}

/**
 * @param {{ companyID: string, emails: string[] }} payload
 */
export async function fetchOrgNfhsAccountStatus(payload) {
  try {
    const { data } = await axios.post(
      `${baseUrl()}/org-account-status`,
      payload,
      requestConfig()
    )
    return {
      success: data?.success !== false,
      accounts: Array.isArray(data?.accounts) ? data.accounts : [],
      missing: Array.isArray(data?.missing) ? data.missing : [],
    }
  } catch (err) {
    throw parseNfhsDigitalError(err)
  }
}

/**
 * @param {{ companyID: string, bookId: string, bookType?: string, emails: string[] }} payload
 */
export async function assignOrgNfhsPublications(payload) {
  try {
    const { data } = await axios.post(
      `${baseUrl()}/org-assign`,
      payload,
      requestConfig()
    )
    return data
  } catch (err) {
    throw parseNfhsDigitalError(err)
  }
}

/**
 * Unassign books this organization assigned. Same body as assign.
 * @param {{
 *   companyID: string,
 *   bookId: string,
 *   bookType?: string,
 *   bookTitle?: string,
 *   bookSport?: string,
 *   bookSportLabel?: string,
 *   emails: string[],
 * }} payload
 */
export async function unassignOrgNfhsPublications(payload) {
  try {
    const { data } = await axios.post(
      `${baseUrl()}/org-unassign`,
      payload,
      requestConfig()
    )
    return data
  } catch (err) {
    throw parseNfhsDigitalError(err)
  }
}

/**
 * @param {string} companyID
 * @param {{ kind?: string } | string} [optionsOrKind] optional kind id (or legacy `{ kind }`)
 */
export async function removeOrgNfhsAddon(companyID, optionsOrKind) {
  const body = { companyID }
  const kind =
    typeof optionsOrKind === 'string'
      ? optionsOrKind
      : optionsOrKind?.kind
  if (kind) body.kind = kind
  try {
    const { data } = await axios.post(
      `${baseUrl()}/remove-addon`,
      body,
      requestConfig()
    )
    return data
  } catch (err) {
    throw parseNfhsDigitalError(err)
  }
}
